import {
  ApplicationConfig,
  inject,
  Injectable,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { httpInterceptor } from '@core/interceptors';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';

import {
  TimeagoClock,
  TimeagoDefaultClock,
  TimeagoDefaultFormatter,
  TimeagoFormatter,
  TimeagoIntl,
} from 'ngx-timeago';

import { provideMapboxGL } from 'ngx-mapbox-gl';

import { environment } from '@env/environment';
import { map, Observable } from 'rxjs';
const { apiUrl } = environment;

/** Fallbacks temporales hasta que las etiquetas sean publicadas por backend. */
const BOOKING_REDESIGN_LABELS: Record<string, any> = {
  es: {
    vr_filter: {
      all: 'Todos',
      video: 'Video VR',
      tour: 'Recorrido VR',
    },
    contains_virtual_reality: 'Contiene realidad virtual',
    $share_location: 'Ver en mapa completo',
    cancelar: 'Cancelar',
    cerrar: 'Cerrar',
    rating_modal: {
      eyebrow: 'Tu opinión',
      title: 'Califica este lugar',
      edit_title: 'Edita tu calificación',
      subtitle: 'Elige una puntuación y, si quieres, cuéntanos cómo fue tu experiencia.',
      question: '¿Qué puntuación le das?',
      selected: 'Tu puntuación',
      required: 'Selecciona al menos una estrella para continuar.',
      comment_label: 'Comentario',
      comment_placeholder: 'Comparte un detalle que pueda ayudar a otros viajeros.',
      optional: 'Opcional',
      submit: 'Publicar calificación',
      update: 'Actualizar calificación',
      edit_button: 'Editar calificación',
      already_rated: 'Ya calificaste',
      ratings: 'calificaciones',
      scale: 'de 1 a 5 estrellas',
      error: 'No pudimos guardar tu calificación. Inténtalo de nuevo.',
    },
    reviews_modal: {
      eyebrow: 'Experiencias',
      title: 'Todas las reseñas',
      preview_title: 'Lo que cuentan los viajeros',
      view_all: 'Ver todas',
      empty: 'Todavía no hay reseñas para este lugar.',
      load_more: 'Cargar más reseñas',
      loading: 'Cargando reseñas…',
    },
    geomarker: {
      bookable_options_title: 'Qué puedes reservar aquí',
      bookable_options_description:
        'Elige un servicio y consulta su disponibilidad sin pasos intermedios.',
      featured_products_title: 'Productos para ti',
      about_place_title: 'Conoce el lugar',
      good_to_know_title: 'Bueno saber',
      location_title: 'Dónde está',
      price_by_service: 'por servicio',
      price_breakdown: 'Ver precio por servicio',
      from_price: 'Desde',
      lowest_price_summary: 'Tarifas desde',
      no_charge_on_booking: 'No se realiza ningún cobro al consultar.',
      contact_help_title: '¿Dudas antes de reservar?',
      contact_help_body: 'Escríbenos por WhatsApp',
      immersive_eyebrow: 'Contenido inmersivo',
      immersive_title: 'Recorre el lugar antes de reservar',
      immersive_description:
        'Explora sus recorridos y videos en realidad virtual para decidir con más seguridad.',
      immersive_cta: 'Iniciar recorrido VR',
      read_more: 'Leer descripción completa',
      read_less: 'Ver menos',
      tourism_types_title: 'Tipos de turismo',
      reviews_title: 'Lo que dicen los viajeros',
      title_card_rating: 'Califica este lugar',
    },
    booking: {
      results_title: 'Reserva tu experiencia',
      results_subtitle: 'Cambia de servicio, fechas y disponibilidad sin salir de esta pantalla.',
      back_to_place: 'Volver al lugar',
      modify_search: 'Modificar búsqueda',
      view_availability: 'Ver disponibilidad',
      options_count: 'opciones disponibles',
      option_singular: 'opción',
      option_plural: 'opciones',
      unit_hour: 'hora',
      unit_day: 'día',
      unit_night: 'noche',
      unit_service: 'servicio',
      from_price: 'Desde',
      no_price: 'Precio no disponible',
      summary_label: 'Resumen de reserva',
      pending_products_eyebrow: 'Reserva no disponible',
      pending_products_title: 'Aún no hay servicios disponibles',
      pending_products_body:
        'El prestador todavía está configurando este lugar. Vuelve pronto para consultar las opciones de reserva.',
      empty_availability_title: 'Sin disponibilidad en estas fechas',
      empty_availability_body: 'Prueba con otras fechas o revisa los demás servicios del lugar.',
      change_dates: 'Cambiar fechas',
      no_services_title: 'Este lugar aún no tiene servicios reservables',
      no_services_body: 'Puedes volver al detalle y explorar el resto de su información.',
      load_error_title: 'No pudimos consultar la disponibilidad',
      load_error_body: 'Revisa tu conexión e inténtalo de nuevo. Tu búsqueda sigue guardada.',
      retry: 'Reintentar',
      invalid_search: 'Completa fechas y horarios válidos para continuar.',
      service_type: 'Tipo de servicio',
      date_label: 'Fecha',
      start_time_label: 'Hora de inicio',
      end_time_label: 'Hora final',
      entry_date_label: 'Entrada',
      exit_date_label: 'Salida',
      open_calendar: 'Abrir calendario',
      guests_label: 'Personas',
      guests_short: 'personas',
      capacity_loading: 'Consultando…',
      capacity_optional: 'Sin especificar',
      decrease_guests: 'Disminuir personas',
      increase_guests: 'Aumentar personas',
      capacity_unavailable: 'No pudimos consultar las capacidades; puedes continuar.',
    },
  },
  en: {
    vr_filter: {
      all: 'All',
      video: 'VR video',
      tour: 'VR tour',
    },
    contains_virtual_reality: 'Includes virtual reality',
    $share_location: 'View full map',
    cancelar: 'Cancel',
    cerrar: 'Close',
    rating_modal: {
      eyebrow: 'Your opinion',
      title: 'Rate this place',
      edit_title: 'Edit your rating',
      subtitle: 'Choose a score and, if you want, tell us how your experience was.',
      question: 'What score would you give it?',
      selected: 'Your score',
      required: 'Select at least one star to continue.',
      comment_label: 'Comment',
      comment_placeholder: 'Share a detail that could help other travelers.',
      optional: 'Optional',
      submit: 'Publish rating',
      update: 'Update rating',
      edit_button: 'Edit rating',
      already_rated: 'Already rated',
      ratings: 'ratings',
      scale: 'from 1 to 5 stars',
      error: 'We could not save your rating. Please try again.',
    },
    reviews_modal: {
      eyebrow: 'Experiences',
      title: 'All reviews',
      preview_title: 'What travelers say',
      view_all: 'View all',
      empty: 'There are no reviews for this place yet.',
      load_more: 'Load more reviews',
      loading: 'Loading reviews…',
    },
    geomarker: {
      bookable_options_title: 'What you can book here',
      bookable_options_description:
        'Choose a service and check availability without intermediate steps.',
      featured_products_title: 'Products for you',
      about_place_title: 'About this place',
      good_to_know_title: 'Good to know',
      location_title: 'Location',
      price_by_service: 'per service',
      price_breakdown: 'View price by service',
      from_price: 'From',
      lowest_price_summary: 'Rates from',
      no_charge_on_booking: 'You will not be charged for checking availability.',
      contact_help_title: 'Questions before booking?',
      contact_help_body: 'Contact us on WhatsApp',
      immersive_eyebrow: 'Immersive content',
      immersive_title: 'Tour the place before booking',
      immersive_description:
        'Explore its virtual reality tours and videos so you can decide with confidence.',
      immersive_cta: 'Start VR tour',
      read_more: 'Read full description',
      read_less: 'Show less',
      tourism_types_title: 'Tourism types',
      reviews_title: 'What travelers say',
      title_card_rating: 'Rate this place',
    },
    booking: {
      results_title: 'Book your experience',
      results_subtitle: 'Change the service, dates and availability without leaving this screen.',
      back_to_place: 'Back to place',
      modify_search: 'Modify search',
      view_availability: 'View availability',
      options_count: 'available options',
      option_singular: 'option',
      option_plural: 'options',
      unit_hour: 'hour',
      unit_day: 'day',
      unit_night: 'night',
      unit_service: 'service',
      from_price: 'From',
      no_price: 'Price unavailable',
      summary_label: 'Booking summary',
      pending_products_eyebrow: 'Booking unavailable',
      pending_products_title: 'No services available yet',
      pending_products_body:
        'The provider is still setting up this place. Check back soon to explore booking options.',
      empty_availability_title: 'No availability for these dates',
      empty_availability_body: 'Try other dates or explore the other services at this place.',
      change_dates: 'Change dates',
      no_services_title: 'This place has no bookable services yet',
      no_services_body: 'Go back to the place page to explore the rest of its information.',
      load_error_title: 'We could not check availability',
      load_error_body: 'Check your connection and try again. Your search is still saved.',
      retry: 'Try again',
      invalid_search: 'Complete valid dates and times to continue.',
      service_type: 'Service type',
      date_label: 'Date',
      start_time_label: 'Start time',
      end_time_label: 'End time',
      entry_date_label: 'Check-in',
      exit_date_label: 'Check-out',
      open_calendar: 'Open calendar',
      guests_label: 'Guests',
      guests_short: 'guests',
      capacity_loading: 'Checking…',
      capacity_optional: 'Not specified',
      decrease_guests: 'Decrease guests',
      increase_guests: 'Increase guests',
      capacity_unavailable: 'We could not load capacities; you can still continue.',
    },
  },
};

// Loader personalizado para evitar problemas de codificación
@Injectable()
export class CustomTranslateLoader implements TranslateLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string): Observable<any> {
    // Construimos la URL manualmente para evitar codificación de caracteres
    const url = `${apiUrl}web-labels/language/${lang}`;
    return this.http.get<Record<string, any>>(url).pipe(
      map((remoteLabels) => ({
        ...(BOOKING_REDESIGN_LABELS[lang] ?? BOOKING_REDESIGN_LABELS['es']),
        ...remoteLabels,
        vr_filter: {
          ...(BOOKING_REDESIGN_LABELS[lang]?.['vr_filter'] ??
            BOOKING_REDESIGN_LABELS['es']['vr_filter']),
          ...(remoteLabels?.['vr_filter'] ?? {}),
        },
        geomarker: {
          ...(BOOKING_REDESIGN_LABELS[lang]?.['geomarker'] ??
            BOOKING_REDESIGN_LABELS['es']['geomarker']),
          ...(remoteLabels?.['geomarker'] ?? {}),
        },
        booking: {
          ...(BOOKING_REDESIGN_LABELS[lang]?.['booking'] ??
            BOOKING_REDESIGN_LABELS['es']['booking']),
          ...(remoteLabels?.['booking'] ?? {}),
        },
      })),
    );
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Zoneless queda pendiente: compila y renderiza SSR igual que con zone.js, pero
    // falta validar en navegador Mapbox, Pannellum y la pasarela de pago (ePayco),
    // que actualizan estado desde callbacks externos.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withFetch(), withInterceptors([httpInterceptor])),
    provideClientHydration(withEventReplay()),
    provideAnimationsAsync(),
    provideMapboxGL({
      accessToken: environment.mapboxKey,
    }),
    provideTranslateService({
      fallbackLang: 'es',
      loader: { provide: TranslateLoader, useClass: CustomTranslateLoader },
    }),
    // Equivale a `TimeagoModule.forRoot()`, sin `importProvidersFrom`.
    TimeagoIntl,
    { provide: TimeagoClock, useClass: TimeagoDefaultClock },
    { provide: TimeagoFormatter, useClass: TimeagoDefaultFormatter },
  ],
};
